import api, { handleApiError } from './client';

const toExactISOString = (dt) => {
  if (!dt) return null;
  if (dt instanceof Date) return dt.toISOString();
  if (typeof dt === 'string') {
    const parsed = new Date(dt);
    if (!isNaN(parsed.getTime())) return parsed.toISOString();
    return dt;
  }
  return new Date(dt).toISOString();
};

export const appointmentService = {
  /**
   * Get appointments with filters
   */
  getAppointments: async ({ start, end, coachId, locationId, groupId } = {}) => {
    try {
      const params = {
        start: start ? toExactISOString(start) : undefined,
        end: end ? toExactISOString(end) : undefined,
        coachId: coachId || undefined,
        locationId: locationId || undefined,
        groupId: groupId || undefined
      };
      const response = await api.get('/api/appointments', { params });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching appointments:', error);
      return handleApiError(error, 'Errore nel caricamento degli appuntamenti');
    }
  },

  /**
   * Get appointment by ID
   */
  getById: async (id) => {
    try {
      const response = await api.get(`/api/appointments/${id}`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching appointment:', error);
      return handleApiError(error, 'Errore nel caricamento dell\'appuntamento');
    }
  },

  /**
   * Pre-check conflicts
   */
  checkConflicts: async (data) => {
    try {
      const payload = {
        ...data,
        startTime: toExactISOString(data.startTime),
        endTime: toExactISOString(data.endTime)
      };
      const response = await api.post('/api/appointments/check-conflicts', payload);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error checking conflicts:', error);
      return handleApiError(error, 'Errore nel controllo dei conflitti');
    }
  },

  /**
   * Create appointment
   */
  create: async (data) => {
    try {
      const payload = {
        ...data,
        startTime: toExactISOString(data.startTime),
        endTime: toExactISOString(data.endTime),
        recurrence: data.recurrence ? {
          ...data.recurrence,
          repeatUntil: data.recurrence.repeatUntil ? toExactISOString(data.recurrence.repeatUntil) : undefined
        } : undefined,
        participants: data.participants ? data.participants.map(p => ({
          ...p,
          paymentDate: p.paymentDate ? toExactISOString(p.paymentDate) : undefined
        })) : []
      };
      const response = await api.post('/api/appointments', payload);
      return { success: true, data: response.data };
    } catch (error) {
      if (error.response && error.response.status === 409) {
        return {
          success: false,
          isConflict: true,
          conflicts: error.response.data,
          message: 'Rilevati conflitti orari per l\'appuntamento.'
        };
      }
      console.error('Error creating appointment:', error);
      return handleApiError(error, 'Errore nella creazione dell\'appuntamento');
    }
  },

  /**
   * Update appointment
   */
  update: async (id, data) => {
    try {
      const payload = {
        ...data,
        startTime: toExactISOString(data.startTime),
        endTime: toExactISOString(data.endTime),
        participants: data.participants ? data.participants.map(p => ({
          ...p,
          paymentDate: p.paymentDate ? toExactISOString(p.paymentDate) : undefined
        })) : undefined
      };
      const response = await api.put(`/api/appointments/${id}`, payload);
      return { success: true, data: response.data };
    } catch (error) {
      if (error.response && error.response.status === 409) {
        return {
          success: false,
          isConflict: true,
          conflicts: error.response.data,
          message: 'Rilevati conflitti orari per la modifica.'
        };
      }
      console.error('Error updating appointment:', error);
      return handleApiError(error, 'Errore nell\'aggiornamento dell\'appuntamento');
    }
  },

  /**
   * Delete appointment
   */
  delete: async (id, deleteSeries = false) => {
    try {
      const response = await api.delete(`/api/appointments/${id}`, {
        params: { deleteSeries }
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error deleting appointment:', error);
      return handleApiError(error, 'Errore nell\'eliminazione dell\'appuntamento');
    }
  },

  /**
   * Submit roll-call for appointment
   */
  submitRollCall: async (id, participants) => {
    try {
      const response = await api.post(`/api/appointments/${id}/roll-call`, { participants });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error submitting appointment roll-call:', error);
      return handleApiError(error, 'Errore nella conferma dell\'appello');
    }
  }
};
